<template>
  <div class="message-send-counts-tab" v-loading="loading">
    <div v-if="prizeId === null" class="message message-info">
      個別の応募グループを選択するとアンケートが表示されます。
    </div>
    <div v-else>
      <div class="table-card__header">
        <CsvDownload :download="downloadCsv" :disabled="loading || total === 0" text="CSVダウンロード" />
      </div>

      <el-table
        v-if="rows.length"
        :data="rows"
        :row-key="row => row.nodeId"
        :default-sort="defaultSort"
        :max-height="tableMaxHeight"
        :loading="loading"
        :cell-class-name="() => 'table__cell'"
        :header-cell-class-name="() => 'table__header-cell'"
        stripe
        style="width: 100%"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="message" label="メッセージ" width="360" header-align="left">
          <template #default="{ row }">
            <div class="text-cell" :title="messageLabel(row.message)">
              {{ messageLabel(row.message) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          prop="messageType"
          column-key="messageType"
          :sortable="'custom'"
          label="種別"
          header-align="left"
          width="140"
        />

        <el-table-column
          prop="receivedCount"
          column-key="receivedCount"
          :sortable="'custom'"
          label="送信数"
          header-align="left"
          width="120"
        >
          <template #default="{ row }">
            <span class="number-cell">{{ row.receivedCount }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-else-if="!loading" class="empty">
        <el-empty description="データがありません。" />
      </div>

      <div class="table-footer" v-if="total > 0">
        <AppPagination :total="total" :current-page="pagingNo" :page-size="limitCount" @paging="handlePaging" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck TODO: 既存の型エラーを解消したら外す
import { computed } from 'vue'

import { AppPagination } from '@/client/components/_atoms'
import CsvDownload from '@/client/components/_atoms/csv_download'

import { InstagramInstantwinMessageSendCountRow, MessageSendCountSortBy } from '../types'

interface Props {
  prizeId: number | null
  rows: InstagramInstantwinMessageSendCountRow[]
  total: number
  pagingNo: number
  limitCount: number
  sortBy: MessageSendCountSortBy | ''
  loading: boolean
  downloadCsv: () => Promise<any> | any
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'sort-change', value: MessageSendCountSortBy | ''): void
  (e: 'paging', payload: { currentPage: number; pageSize: number }): void
}>()

const defaultSort = computed(() => {
  if (!props.sortBy) return undefined
  const key = props.sortBy.startsWith('-') ? props.sortBy.slice(1) : props.sortBy
  const order = props.sortBy.startsWith('-') ? 'descending' : 'ascending'
  return { prop: key, order }
})

const handleSortChange = (payload: {
  column?: { columnKey?: string; property?: string }
  prop?: string
  order?: 'ascending' | 'descending'
}) => {
  if (props.loading) return
  const key = payload.column?.columnKey || payload.column?.property || payload.prop
  if (!key) return
  if (!payload.order) {
    emit('sort-change', '')
    return
  }
  const sortKey =
    payload.order === 'descending' ? (`-${key}` as MessageSendCountSortBy) : (key as MessageSendCountSortBy)
  emit('sort-change', sortKey)
}

const handlePaging = (payload: { currentPage: number; pageSize: number }) => {
  emit('paging', payload)
}

const messageLabel = (value: string | null) => {
  return value && value.trim().length > 0 ? value : '-'
}

const tableMaxHeight = 400
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.message-send-counts-tab {
  width: 100%;
}

.table-card {
  width: 100%;
}

.table-card__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: $spacing-unit * 2;
}

.table-card__meta {
  font-size: 14px;
  color: #5f6368;
}

.table__header-cell {
  padding: 12px 14px !important;
  font-size: 13px;
  font-weight: 600;
  color: #5f6368;
  text-align: left;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table__cell {
  padding: 12px 14px !important;
  text-align: left;
}

.text-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  color: #3f434a;
  white-space: nowrap;
}

.number-cell {
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  color: #32363f;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: $spacing-unit * 2;
}

.empty {
  padding: 24px 0;
}
</style>
